import { useEffect, useMemo } from 'react'
import * as THREE from 'three'
import type { EstadoManejoVidro } from '../../dominio/didatico/manejoVidros'

/** Gerador determinístico: o mesmo vidro trinca sempre do mesmo jeito. */
function sorteio(semente: number) {
  let s = semente >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 2 ** 32
  }
}

/**
 * Trincas de um vidro de `largura` × `altura`, como segmentos de linha.
 *
 * Teia de aranha a partir de um ponto de impacto: raios que se ramificam até a
 * borda e anéis irregulares ligando os raios. Com `mosaico`, o vidro inteiro
 * ganha trincas curtas — é o vidro craquelado que a película segura no lugar.
 */
export function geometriaTrincas(largura: number, altura: number, semente: number, mosaico: boolean): THREE.BufferGeometry {
  const aleatorio = sorteio(semente)
  const mx = largura / 2
  const my = altura / 2
  const cx = (aleatorio() - 0.5) * largura * 0.4
  const cy = (aleatorio() - 0.5) * altura * 0.4
  const pontos: number[] = []
  const segmento = (x1: number, y1: number, x2: number, y2: number) => pontos.push(x1, y1, 0, x2, y2, 0)
  const dentro = (x: number, y: number) => Math.abs(x) <= mx && Math.abs(y) <= my

  const RAIOS = 13
  const ANEIS = [0.06, 0.14, 0.26]
  const passosPorRaio: Array<Array<[number, number]>> = []
  for (let i = 0; i < RAIOS; i += 1) {
    let angulo = (i / RAIOS) * Math.PI * 2 + (aleatorio() - 0.5) * 0.35
    let x = cx
    let y = cy
    const caminho: Array<[number, number]> = [[x, y]]
    for (let passo = 0; passo < 40; passo += 1) {
      angulo += (aleatorio() - 0.5) * 0.25
      const nx = x + Math.cos(angulo) * 0.045
      const ny = y + Math.sin(angulo) * 0.045
      if (!dentro(nx, ny)) break
      segmento(x, y, nx, ny)
      x = nx
      y = ny
      caminho.push([x, y])
      // ramificação ocasional, curta
      if (aleatorio() < 0.08) {
        const ra = angulo + (aleatorio() < 0.5 ? 0.6 : -0.6)
        const bx = x + Math.cos(ra) * 0.08
        const by = y + Math.sin(ra) * 0.08
        if (dentro(bx, by)) segmento(x, y, bx, by)
      }
    }
    passosPorRaio.push(caminho)
  }
  // anéis: liga o ponto de cada raio que está mais perto do raio do anel
  for (const r of ANEIS) {
    const naCirculo = passosPorRaio.map((caminho) => {
      let melhor = caminho[0]!
      for (const p of caminho) {
        if (Math.abs(Math.hypot(p[0] - cx, p[1] - cy) - r) < Math.abs(Math.hypot(melhor[0] - cx, melhor[1] - cy) - r)) melhor = p
      }
      return melhor
    })
    for (let i = 0; i < naCirculo.length; i += 1) {
      const a = naCirculo[i]!
      const b = naCirculo[(i + 1) % naCirculo.length]!
      if (Math.hypot(a[0] - cx, a[1] - cy) > r * 0.6 && Math.hypot(b[0] - cx, b[1] - cy) > r * 0.6) segmento(a[0], a[1], b[0], b[1])
    }
  }
  if (mosaico) {
    const quantidade = Math.round(largura * altura * 260)
    for (let i = 0; i < quantidade; i += 1) {
      const x = (aleatorio() - 0.5) * largura
      const y = (aleatorio() - 0.5) * altura
      const angulo = aleatorio() * Math.PI
      const c = 0.015 + aleatorio() * 0.03
      const x2 = x + Math.cos(angulo) * c
      const y2 = y + Math.sin(angulo) * c
      if (dentro(x2, y2)) segmento(x, y, x2, y2)
    }
  }
  const geometria = new THREE.BufferGeometry()
  geometria.setAttribute('position', new THREE.Float32BufferAttribute(pontos, 3))
  return geometria
}

/** Contorno tracejado junto à borda: o caminho da serra no vidro cortado. */
function geometriaCorte(largura: number, altura: number): THREE.BufferGeometry {
  const recuo = 0.04
  const mx = largura / 2 - recuo
  const my = altura / 2 - recuo
  const cantos: Array<[number, number]> = [[-mx, -my], [mx, -my], [mx, my], [-mx, my]]
  const pontos: number[] = []
  const TRACO = 0.05
  for (let i = 0; i < 4; i += 1) {
    const [x1, y1] = cantos[i]!
    const [x2, y2] = cantos[(i + 1) % 4]!
    const comprimento = Math.hypot(x2 - x1, y2 - y1)
    for (let d = 0; d < comprimento; d += TRACO * 2) {
      const a = d / comprimento
      const b = Math.min(1, (d + TRACO) / comprimento)
      pontos.push(x1 + (x2 - x1) * a, y1 + (y2 - y1) * a, 0, x1 + (x2 - x1) * b, y1 + (y2 - y1) * b, 0)
    }
  }
  const geometria = new THREE.BufferGeometry()
  geometria.setAttribute('position', new THREE.Float32BufferAttribute(pontos, 3))
  return geometria
}

const semRaycast = () => null

/**
 * Um vidro do veículo que não está mais intacto, desenhado sobre a casca.
 * Cada estado tem a sua leitura — quebrado, craquelado com película, cortado ou
 * removido — em vez de uma mesma grade para todos.
 */
export function VidroDanificado({
  estado,
  largura,
  altura,
  semente,
}: {
  estado: Exclude<EstadoManejoVidro, 'intacto'>
  largura: number
  altura: number
  semente: number
}) {
  const linhas = useMemo(() => {
    if (estado === 'quebrado') return geometriaTrincas(largura, altura, semente, false)
    if (estado === 'craqueladoComPelicula') return geometriaTrincas(largura, altura, semente, true)
    if (estado === 'cortado') return geometriaCorte(largura, altura)
    return null
  }, [estado, largura, altura, semente])
  useEffect(() => () => linhas?.dispose(), [linhas])

  if (estado === 'removido') {
    // a abertura: escura, como o vão sem vidro visto de fora
    return (
      <mesh raycast={semRaycast} position={[0, 0, 0.003]}>
        <planeGeometry args={[largura * 0.96, altura * 0.96]} />
        <meshBasicMaterial color="#0b1016" transparent opacity={0.55} side={2} depthWrite={false} />
      </mesh>
    )
  }
  return (
    <group>
      {/* o vidro trincado perde a transparência e fica leitoso */}
      <mesh raycast={semRaycast} position={[0, 0, 0.002]}>
        <planeGeometry args={[largura * 0.98, altura * 0.98]} />
        <meshStandardMaterial
          color="#dbe9f2"
          roughness={0.4}
          metalness={0.1}
          transparent
          opacity={estado === 'cortado' ? 0.18 : estado === 'craqueladoComPelicula' ? 0.5 : 0.32}
          side={2}
          depthWrite={false}
        />
      </mesh>
      {linhas && (
        <lineSegments raycast={semRaycast} geometry={linhas} position={[0, 0, 0.005]}>
          <lineBasicMaterial color={estado === 'cortado' ? '#f97316' : '#ffffff'} transparent opacity={0.85} />
        </lineSegments>
      )}
    </group>
  )
}
